/**
 * Year6SuccessPage — /year6/:slug/success
 *
 * Post-payment celebration. Download CTA + viral share to other parents
 * + "watch highlights too" bonus engagement.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, DownloadSimple, ShareNetwork, FilmReel, WhatsappLogo, Copy, Sparkle, GraduationCap } from '@phosphor-icons/react';
import { stampDownloadedVersion } from '../components/Year6NewContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Year6SuccessPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [eventId, setEventId] = useState(null);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const r = await axios.get(`${API}/events/slug/${slug}`); if (!cancelled) setEventId(r.data.event_id); }
      catch { /* */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!eventId) return;
    let tries = 0;
    const tick = async () => {
      try {
        const [r, s] = await Promise.all([
          axios.get(`${API}/events/${eventId}/year6/parent-view-data`),
          axios.get(`${API}/events/${eventId}/year6/stats`).catch(() => null),
        ]);
        setData(r.data);
        if (s?.data) setStats(s.data);
        tries++;
        // If we just came back from Stripe and webhook hasn't flipped yet, keep polling
        if (!r.data.memory_book_purchased && searchParams.get('session_id') && tries < 15) {
          setTimeout(tick, 2000);
        }
      } catch { /* */ }
    };
    tick();
  }, [eventId, searchParams]);

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/year6/${slug}/view` : '';
  const shareText = `We've just created our Year 6 Memory Book 🎓\nYou can view and download it here:\n${shareUrl}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  const trackShare = (channel) => {
    if (!eventId) return;
    let anonId = null;
    try { anonId = localStorage.getItem('anon_id'); } catch { /* */ }
    axios.post(`${API}/events/${eventId}/year6/track-parent-share`, { channel, anon_id: anonId })
      .catch(() => { /* non-blocking */ });
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareText); toast.success('Link copied!'); trackShare('copy'); }
    catch { toast.error('Could not copy'); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    // Stamp the version we're about to download so the "Updated since last download" badge clears
    stampDownloadedVersion(eventId, stats?.memory_book_version);
    // 1.2s perceived-value delay per spec
    setTimeout(() => {
      // Use the new editorial Year 6 generator (cover/hero/grid/messages/last-moments/closing).
      window.open(`${API}/events/${eventId}/year6/memory-book/download`, '_blank');
      setDownloading(false);
      toast.message('Tip: Save this somewhere safe — you can re-download anytime.');
    }, 1200);
  };

  const purchased = data?.memory_book_purchased;

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="year6-success-page">
      <div className="max-w-xl mx-auto px-5 pt-12 pb-20">
        <div className="text-center mb-8">
          <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300 mb-5 mx-auto">
            <Check size={32} weight="bold" />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200 text-[10px] font-black uppercase tracking-[0.18em] mb-4">
            <GraduationCap size={11} weight="fill" /> Memory Book unlocked
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-black leading-[1.05] mb-3" data-testid="year6-success-title">
            🎉 Your Memory Book<br/>is ready!
          </h1>
          <p className="text-white/65">Download it now and keep it forever.</p>
        </div>

        {/* Confirming purchase if Stripe webhook not yet fired */}
        {!purchased && searchParams.get('session_id') && (
          <div className="mb-5 p-3 rounded-xl bg-amber-300/10 border border-amber-300/25 text-amber-200 text-sm flex items-center gap-2" data-testid="year6-success-confirming">
            <Sparkle size={16} weight="fill" className="animate-pulse" /> Confirming your payment — this usually takes a few seconds.
          </div>
        )}

        {/* Primary action */}
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-black text-white shadow-2xl mb-3 hover:scale-[1.01] transition-transform disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
          data-testid="year6-success-download"
        >
          <DownloadSimple size={18} weight="bold" />
          {downloading ? 'Generating your Memory Book…' : 'Download Memory Book'}
        </button>

        {/* Memory Book v2 — set the expectation that this isn't finished */}
        <p
          className="text-center text-[12.5px] text-[#C29D52]/85/85 mb-4 inline-flex items-center justify-center gap-1.5 w-full"
          data-testid="year6-success-updates-expectation"
        >
          <span aria-hidden="true">✨</span>
          {"You\u2019ll get updates if new memories are added"}
        </p>

        {/* Highlight CTA */}
        {data?.highlight_url && (
          <a
            href={`/event/${slug}?focus=highlight`}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold border border-[#B89762]/45/30 bg-[#B89762]/40/8 text-[#C29D52]/85 hover:bg-[#B89762]/85/12 mb-6"
            data-testid="year6-success-watch-highlights"
          >
            <FilmReel size={14} weight="fill" /> 🎬 Your highlight video is ready too
          </a>
        )}

        {/* Viral loop — share with other parents */}
        <div className="rounded-2xl p-5 border border-white/10 bg-white/[0.03] mt-6" data-testid="year6-success-viral">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200 mb-2 inline-flex items-center gap-1.5">
            <ShareNetwork size={11} weight="bold" /> Share with other parents
          </p>
          <p className="text-white/65 text-sm mb-4">Other parents can also access the Memory Book.</p>
          <div className="grid grid-cols-2 gap-2.5">
            <a onClick={() => trackShare('whatsapp')} href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-black text-white" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }} data-testid="year6-success-share-whatsapp">
              <WhatsappLogo size={13} weight="fill" /> WhatsApp
            </a>
            <button onClick={copyLink} className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-black bg-white text-neutral-900 hover:bg-neutral-100" data-testid="year6-success-share-copy">
              <Copy size={13} weight="bold" /> Copy link
            </button>
          </div>
        </div>

        <p className="text-center text-[11px] text-white/40 mt-6">Tip: Save this somewhere safe — you can re-download anytime from this link.</p>
      </div>
    </div>
  );
}
